/**
 * DevModeContext - Development mode authentication for testing
 * 
 * Provides mock user sessions for testing without requiring local Supabase.
 * 
 * Usage:
 *   - Add ?devMode=creator to URL for creator mock session
 *   - Add ?devMode=developer for developer mock session
 *   - etc.
 * 
 * ⚠️ SECURITY: 
 *   - ENABLED: localhost, staging (VITE_APP_ENVIRONMENT=staging)
 *   - DISABLED: preview, production, and all other remote environments
 */

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import type { 
  DevModeContextValue, 
  DevModeProviderProps, 
  DevModeRole,
  DevModeUser,
} from './types';
import { DEFAULT_SECURITY_CONFIG } from './types';
import { getMockUsersForApp, isValidRoleForApp } from './mock-users';

const DevModeContext = createContext<DevModeContextValue | null>(null);

/**
 * Check if dev mode is allowed in the current environment
 * 
 * SECURITY: Dev mode is ONLY enabled on:
 *   - localhost/127.0.0.1/0.0.0.0 (local development)
 *   - VITE_APP_ENVIRONMENT === 'staging' (staging.lykbl.com, staging.lykbl.ai)
 * 
 * DISABLED on:
 *   - production (VITE_APP_ENVIRONMENT === 'production')
 *   - preview/beta (VITE_APP_ENVIRONMENT === 'preview' / beta.lykbl.com)
 *   - any other remote environment
 * 
 * RATIONALE: Preview is a persistent, shared environment accessible to external testers.
 * Enabling dev mode would allow mock authentication bypass on a public URL, which is a security risk.
 */
export const isDevModeAllowed = (): boolean => {
  // CRITICAL SECURITY: Block production domains FIRST, before any other checks
  // This is a fail-safe that works even if environment variables are misconfigured
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const appEnv = (import.meta as any).env?.VITE_APP_ENVIRONMENT;
  
  // DEBUG: Log environment info
  console.log('[DevMode] Checking environment:', {
    hostname,
    isDEV: import.meta.env.DEV,
    appEnv,
    mode: import.meta.env.MODE,
  });
  
  // Block all production domains - this check happens FIRST
  if (DEFAULT_SECURITY_CONFIG.productionDomains.includes(hostname)) {
    console.warn('🚫 Dev mode is disabled on production domains');
    return false;
  }
  
  // Block all preview/beta domains
  if (DEFAULT_SECURITY_CONFIG.previewDomains.includes(hostname)) {
    console.warn('🚫 Dev mode is disabled on preview/beta domains');
    return false;
  }
  
  // Block Cloudflare Pages preview deployments (*.pages.dev)
  // EXCEPT when explicitly in staging environment OR on a staging-prefixed domain
  const isStagingDomain = DEFAULT_SECURITY_CONFIG.stagingDomains.includes(hostname);
  if (hostname.endsWith('.pages.dev') && appEnv !== 'staging' && !isStagingDomain) {
    console.warn('🚫 Dev mode is disabled on Cloudflare Pages preview deployments');
    return false;
  }
  
  // PRODUCTION: Always disabled, no exceptions
  if (appEnv === 'production') {
    console.warn('🚫 Dev mode disabled: production environment');
    return false;
  }
  
  // PREVIEW/BETA: Disabled - persistent shared environment
  if (appEnv === 'preview') {
    console.warn('🚫 Dev mode disabled: preview environment');
    return false;
  }
  
  // LOCAL DEVELOPMENT: Vite dev server - always allowed
  if (import.meta.env.DEV) {
    console.log('🔧 Dev mode enabled: Vite dev server (import.meta.env.DEV === true)');
    return true;
  }
  
  // STAGING: Explicitly allowed for testing
  if (appEnv === 'staging') {
    console.log('🔧 Dev mode enabled: Staging environment');
    return true;
  }
  
  // LOCALHOST FALLBACK: Allow on localhost even without DEV flag
  if (['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname)) {
    console.log('🔧 Dev mode enabled: Localhost hostname detected');
    return true;
  }
  
  // Staging domain fallback (if env var not set)
  if (DEFAULT_SECURITY_CONFIG.stagingDomains.includes(hostname)) {
    console.log('🔧 Dev mode enabled: Staging domain detected');
    return true;
  }
  
  // DEFAULT: Disabled for any unrecognized environment (fail-safe)
  console.warn('🚫 Dev mode disabled: Unrecognized environment', { hostname, appEnv });
  return false;
};

/**
 * Check if current environment is staging (requires auth gate)
 */
export const isStaging = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const appEnv = import.meta.env.VITE_APP_ENVIRONMENT;
  
  return DEFAULT_SECURITY_CONFIG.stagingDomains.includes(hostname) || appEnv === 'staging';
};

// Create a mock Supabase User object
const createMockUser = (devUser: DevModeUser): User => ({
  id: devUser.id,
  email: devUser.email,
  app_metadata: {
    provider: 'email',
    providers: ['email'],
  },
  user_metadata: {
    display_name: devUser.displayName,
    role: devUser.role,
    avatar_url: devUser.avatarUrl,
  },
  aud: 'authenticated',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  role: 'authenticated',
  confirmation_sent_at: undefined,
  confirmed_at: new Date().toISOString(),
  email_confirmed_at: new Date().toISOString(),
  phone: undefined,
  phone_confirmed_at: undefined,
  last_sign_in_at: new Date().toISOString(),
  identities: [],
  factors: [],
});

// Create a mock Supabase Session object
const createMockSession = (user: User): Session => ({
  access_token: `dev-mode-token-${user.id}`,
  refresh_token: `dev-mode-refresh-${user.id}`,
  expires_in: 3600,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
  token_type: 'bearer',
  user,
});

export const DevModeProvider: React.FC<DevModeProviderProps> = ({ children, appType }) => {
  const [devRole, setDevRoleState] = useState<DevModeRole | null>(null);
  const [initialized, setInitialized] = useState(false);
  const isAllowed = isDevModeAllowed();

  // Check URL params on mount
  useEffect(() => {
    if (!isAllowed) {
      setInitialized(true);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const devModeParam = params.get('devMode');
    const mockUsers = getMockUsersForApp(appType);
    
    if (devModeParam && isValidRoleForApp(devModeParam, appType)) {
      setDevRoleState(devModeParam as DevModeRole);
      
      // Store in sessionStorage to persist across navigation
      sessionStorage.setItem(`lykbl:devMode:${appType}`, devModeParam);
      
      console.log(`🔧 Dev Mode activated: ${devModeParam}`);
      console.log(`📧 Mock user: ${mockUsers[devModeParam as keyof typeof mockUsers].email}`);
    } else {
      // Check sessionStorage for existing dev mode
      const storedDevMode = sessionStorage.getItem(`lykbl:devMode:${appType}`);
      if (storedDevMode && isValidRoleForApp(storedDevMode, appType)) {
        setDevRoleState(storedDevMode as DevModeRole);
      }
    }
    
    setInitialized(true);
  }, [isAllowed, appType]);

  const setDevRole = (role: DevModeRole | null) => {
    if (!isAllowed) return;
    
    setDevRoleState(role);
    if (role) {
      sessionStorage.setItem(`lykbl:devMode:${appType}`, role);
    } else {
      sessionStorage.removeItem(`lykbl:devMode:${appType}`);
    }
  };

  const exitDevMode = () => {
    setDevRoleState(null);
    sessionStorage.removeItem(`lykbl:devMode:${appType}`);
    
    // Remove devMode from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('devMode');
    window.history.replaceState({}, '', url.toString());
  };

  const value = useMemo<DevModeContextValue>(() => {
    const isDevMode = devRole !== null && isAllowed;
    const mockUsers = getMockUsersForApp(appType);
    const devUser = isDevMode && devRole && isValidRoleForApp(devRole, appType) 
      ? mockUsers[devRole as keyof typeof mockUsers] 
      : null;
    const mockUser = devUser ? createMockUser(devUser) : null;
    const mockSession = mockUser ? createMockSession(mockUser) : null;

    return {
      isDevMode,
      devUser,
      devRole,
      mockUser,
      mockSession,
      setDevRole,
      exitDevMode,
      isAllowed,
      appType,
    };
  }, [devRole, isAllowed, appType]);

  // Don't render until initialized to avoid flash
  if (!initialized) {
    return null;
  }

  return (
    <DevModeContext.Provider value={value}>
      {children}
    </DevModeContext.Provider>
  );
};

export const useDevMode = (): DevModeContextValue => {
  const context = useContext(DevModeContext);
  if (!context) {
    // Return safe defaults if not wrapped in provider
    return {
      isDevMode: false,
      devUser: null,
      devRole: null,
      mockUser: null,
      mockSession: null,
      setDevRole: () => {},
      exitDevMode: () => {},
      isAllowed: false,
      appType: 'ai-amp',
    };
  }
  return context;
};

export default DevModeProvider;
